import { DepthLightCustom } from '@repo/go-icons';
import { TableCell } from '@repo/go-table';
import type { GridColDef } from '@repo/go-table';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { depthSortedParameters, nameDictionary, timeSortedParametersNoAutoRecognized } from '../../shared/baseParams';
import { Tag, type TStatus } from '../../shared/components/Tag/Tag';
import type { TimeZonesStore } from '../../shared/stores/TimeZonesStore';
import { convertDepthUnitValue } from '../../shared/utils';
import tableStyles from '../AlertsManagerTable.module.css';
import { TableHeader } from '../components/TableHeader/TableHeader';
import { DRILLING_LOADS_MNEMONICS } from '../constants';
import type { TIndexType, TRiskLevel, TSourceType, TOption } from '../types';
import { uniqueOptions } from '../utils';

import type { AutoAnomaliesService } from './AutoAnomaliesService';
import { FIELD_TO_AUTO_ANOMALIES_COLUMN_TITLE_KEY } from './FIELD_TO_AUTO_ANOMALIES_COLUMN_TITLE_KEY';
import { getColumCapabilities } from './filter-utils';
import type { TAutoAnomalyRowData } from './types';
import { AUTO_ANOMALIES_GROUPED_COLUMN_FIELDS } from './useColumnGroups';

const riskToStatus = (risk: TRiskLevel): TStatus => {
  switch (risk) {
    case 'HIGH_RISK':
      return 'red';
    case 'MED_RISK':
      return 'orange';
    case 'LOW_RISK':
      return 'blue';
    case 'NO_RISK':
      return 'gray';
  }
};

export const useAutoAnomaliesColumns = (
  timeZonesStore: TimeZonesStore,
  service: AutoAnomaliesService,
  isAltNames: boolean,
  defaultDepthUnit: 'm' | 'ft',
) => {
  const { t } = useTranslation();
  const getDateWithTimeZone = timeZonesStore.getDate;
  const scaleDepthUnit = service.settingsStore.scaleDepthUnit;
  const editing = service.uiState.editing;
  const parameterTypes = service.parameterTypes;

  const riskLabels = useMemo(
    () => ({
      NO_RISK: t('alertsManagerWidget:riskLevelOptions.NO_RISK'),
      LOW_RISK: t('alertsManagerWidget:riskLevelOptions.LOW_RISK'),
      MED_RISK: t('alertsManagerWidget:riskLevelOptions.MED_RISK'),
      HIGH_RISK: t('alertsManagerWidget:riskLevelOptions.HIGH_RISK'),
    }),
    [t],
  );

  const riskLevelOptions: TOption[] = useMemo(
    () => [
      { label: t('alertsManagerWidget:riskLevelOptions.NO_RISK'), value: 'NO_RISK' },
      { label: t('alertsManagerWidget:riskLevelOptions.LOW_RISK'), value: 'LOW_RISK' },
      { label: t('alertsManagerWidget:riskLevelOptions.MED_RISK'), value: 'MED_RISK' },
      { label: t('alertsManagerWidget:riskLevelOptions.HIGH_RISK'), value: 'HIGH_RISK' },
    ],
    [t],
  );

  const parameterTypeByMnemonic = useMemo(() => {
    return parameterTypes.reduce((acc, parameterType) => {
      const displayMnemonic = parameterType.data['displayMnemonic'];
      acc.set(displayMnemonic as string, parameterType);

      return acc;
    }, new Map<string, (typeof parameterTypes)[number]>());
  }, [parameterTypes]);

  const drillingLoadsOptions = useMemo(() => {
    return DRILLING_LOADS_MNEMONICS.map((mnemonic) => {
      const parameterType = parameterTypeByMnemonic.get(mnemonic);
      const generalName = parameterType?.data['generalName'];
      const name: string = isAltNames && !!generalName ? (generalName as string) : mnemonic;

      return {
        label: name,
        value: mnemonic,
      };
    });
  }, [isAltNames, parameterTypeByMnemonic]);

  return useMemo((): GridColDef<TAutoAnomalyRowData>[] => {
    const baseColumn: Omit<GridColDef, 'field'> = {
      resizable: true,
      disableReorder: !editing,
      minWidth: 61,
      filterable: false,
      sortable: false,
      headerAlign: 'left',
      renderHeader: (params) => (
        <TableHeader
          field={params.field}
          textTKey={FIELD_TO_AUTO_ANOMALIES_COLUMN_TITLE_KEY[params.field] ?? params.field}
          tableSubHeader={AUTO_ANOMALIES_GROUPED_COLUMN_FIELDS.has(params.field)}
        />
      ),
      renderCell: (params) => {
        return <div style={{ padding: '0 10px' }}>{params.formattedValue}</div>;
      },
    };

    const numberColumn: Omit<GridColDef, 'field'> = {
      ...baseColumn,
      align: 'right',
      valueFormatter: (value) => {
        const number = Number(value);
        if (Number.isNaN(number) || !Number.isFinite(number)) {
          return value;
        }
        return `${Math.round(number * 100) / 100}`;
      },
    };

    function getSourceTypeOptions(source: TSourceType, indexType: TIndexType): TOption[] {
      if (source === 'GENERAL_PARAMETERS') {
        const parameters = indexType === 'LOG_TIME' ? timeSortedParametersNoAutoRecognized : depthSortedParameters;
        return parameters.map((parameter) => ({
          label: t(`alertsManagerWidget:baseParameters.${nameDictionary[parameter]}`),
          value: parameter,
        }));
      }
      if (source === 'AUTOSUPERVISOR_OPERATIONS') {
        return service.autosupervisorOperations.map((operation) => ({
          label: operation.data['name']?.toString() ?? '',
          value: operation.data.code as number,
        }));
      }
      if (source === 'AUTOSUPERVISOR_KPI_STATES') {
        return service.autosupervisorKPIstates.map((kpi) => ({
          label: kpi.data['name']?.toString() ?? '',
          value: kpi.data.code as number,
        }));
      }
      if (source === 'DRILLING_LOADS') {
        return drillingLoadsOptions;
      }
      return [];
    }

    function getSourceTypeValueOptions(indexType: TIndexType): TOption[] {
      return [
        {
          value: 'GENERAL_PARAMETERS' as const,
          label: t('alertsManagerWidget:autoAnomalies.sourceDataType.GENERAL_PARAMETERS'),
        },
        indexType === 'LOG_DEPTH'
          ? {
              value: 'DRILLING_LOADS' as const,
              label: t('alertsManagerWidget:autoAnomalies.sourceDataType.DRILLING_LOADS'),
            }
          : null,
        indexType === 'LOG_TIME'
          ? {
              value: 'AUTOSUPERVISOR_OPERATIONS' as const,
              label: t('alertsManagerWidget:autoAnomalies.sourceDataType.AUTOSUPERVISOR_OPERATIONS'),
            }
          : null,
        indexType === 'LOG_TIME'
          ? {
              value: 'AUTOSUPERVISOR_KPI_STATES' as const,
              label: t('alertsManagerWidget:autoAnomalies.sourceDataType.AUTOSUPERVISOR_KPI_STATES'),
            }
          : null,
      ].filter((option): option is NonNullable<typeof option> => option != null);
    }

    function getAllSourceTypeValueOptions(): TOption[] {
      return uniqueOptions([...getSourceTypeValueOptions('LOG_TIME'), ...getSourceTypeValueOptions('LOG_DEPTH')]);
    }

    function getAllSourceTypeOptions(): TOption[] {
      return uniqueOptions([
        ...getSourceTypeOptions('GENERAL_PARAMETERS', 'LOG_TIME'),
        ...getSourceTypeOptions('GENERAL_PARAMETERS', 'LOG_DEPTH'),
        ...getSourceTypeOptions('AUTOSUPERVISOR_OPERATIONS', 'LOG_TIME'),
        ...getSourceTypeOptions('AUTOSUPERVISOR_KPI_STATES', 'LOG_TIME'),
        ...getSourceTypeOptions('DRILLING_LOADS', 'LOG_DEPTH'),
      ]);
    }

    const eventZoned = (row: TAutoAnomalyRowData) => {
      const d = new Date(row.anomalyAt);
      if (Number.isNaN(d.valueOf())) {
        return null;
      }

      return getDateWithTimeZone(d.valueOf());
    };

    return [
      {
        field: 'anomalyAt',
        type: 'dateTime',
        ...getColumCapabilities('anomalyAt'),
        resizable: true,
        disableReorder: !editing,
        minWidth: 100,
        flex: 0.5,
        headerAlign: 'left',
        align: 'left',
        valueGetter: (_, row) => eventZoned(row),
        renderHeader: (params) => (
          <TableHeader field={params.field} textTKey={FIELD_TO_AUTO_ANOMALIES_COLUMN_TITLE_KEY.anomalyAt} />
        ),
        valueFormatter: (value) => {
          return value ? format(value, 'dd.MM.yyyy') : '-';
        },
      },
      {
        field: 'eventTime',
        type: 'date',
        filterable: false,
        sortable: false,
        resizable: true,
        disableReorder: !editing,
        minWidth: 100,
        flex: 0.5,
        headerAlign: 'left',
        align: 'left',
        valueGetter: (_, row) => eventZoned(row),
        renderHeader: (params) => (
          <TableHeader field={params.field} textTKey={FIELD_TO_AUTO_ANOMALIES_COLUMN_TITLE_KEY.eventTime} />
        ),
        valueFormatter: (value) => {
          return value ? format(value, 'HH:mm:ss') : '-';
        },
      },
      {
        ...baseColumn,
        field: 'wellboreId',
        type: 'singleSelect',
        ...getColumCapabilities('wellboreId'),
        valueOptions: service.wellbores.list.map((wellbore) => ({
          value: wellbore.id,
          label: wellbore.specialName,
        })),
        minWidth: 120,
      },
      {
        field: 'riskLevel',
        resizable: false,
        type: 'singleSelect',
        ...getColumCapabilities('riskLevel'),
        disableReorder: !editing,
        width: 120,
        minWidth: 120,
        maxWidth: 120,
        headerAlign: 'left',
        align: 'left',
        valueGetter: (_, row) => row.riskLevel,
        valueOptions: riskLevelOptions,
        renderHeader: (params) => (
          <TableHeader field={params.field} textTKey={FIELD_TO_AUTO_ANOMALIES_COLUMN_TITLE_KEY.riskLevel} />
        ),
        renderCell(params) {
          const risk = params.value as TRiskLevel;
          return (
            <TableCell>
              <Tag status={riskToStatus(risk)} block style={{ width: '100%' }}>
                {riskLabels[risk] ?? risk}
              </Tag>
            </TableCell>
          );
        },
      },
      {
        ...baseColumn,
        field: 'updatedBy',
        type: 'string',
        ...getColumCapabilities('updatedBy'),
        minWidth: 120,
        editable: false,
        valueGetter: (_, row) => row.updatedBy?.displayName,
      },
      {
        ...baseColumn,
        field: 'anomalyTypeId',
        type: 'singleSelect',
        ...getColumCapabilities('anomalyTypeId'),
        minWidth: 120,
        valueOptions: service.anomalyTypes.map((anomalyType) => ({
          value: anomalyType.id,
          label: anomalyType.data['name']?.toString() ?? '',
        })),
      },
      {
        ...baseColumn,
        field: 'anomalySubtypeId',
        type: 'singleSelect',
        ...getColumCapabilities('anomalySubtypeId'),
        minWidth: 120,
        valueOptions: service.anomalySubtypes.map((anomalySubtype) => ({
          value: anomalySubtype.id,
          label: anomalySubtype.data['name']?.toString() ?? '',
        })),
      },
      {
        ...baseColumn,
        field: 'indexType',
        type: 'singleSelect',
        ...getColumCapabilities('indexType'),
        minWidth: 120,
        valueOptions: [
          { value: 'LOG_TIME', label: t('alertsManagerWidget:autoAnomalies.indexFormat.time') },
          { value: 'LOG_DEPTH', label: t('alertsManagerWidget:autoAnomalies.indexFormat.depth') },
        ],
      },
      {
        ...baseColumn,
        field: 'sourceType',
        type: 'singleSelect',
        ...getColumCapabilities('sourceType'),
        minWidth: 120,
        valueOptions: (params) =>
          params.row?.indexType ? getSourceTypeValueOptions(params.row.indexType) : getAllSourceTypeValueOptions(),
      },
      {
        ...baseColumn,
        field: 'sourceOption',
        type: 'singleSelect',
        ...getColumCapabilities('sourceOption'),
        minWidth: 120,
        valueOptions: (params) =>
          params.row && params.row.sourceType
            ? getSourceTypeOptions(params.row.sourceType, params.row.indexType)
            : getAllSourceTypeOptions(),
      },
      {
        ...baseColumn,
        field: 'bitDepth',
        type: 'number',
        ...getColumCapabilities('bitDepth'),
        valueFormatter(_value) {
          if (_value == null) {
            return '-';
          }

          const value = Number(_value);

          if (!Number.isNaN(value)) {
            return Math.round(convertDepthUnitValue(value, defaultDepthUnit, scaleDepthUnit) * 100) / 100;
          }

          return '-';
        },
        renderHeader: (params) => (
          <TableHeader
            field={params.field}
            textTKey={FIELD_TO_AUTO_ANOMALIES_COLUMN_TITLE_KEY.bitDepth}
            unit="m"
            icon={<DepthLightCustom />}
          />
        ),
      },
      {
        ...baseColumn,
        field: 'holeDepth',
        type: 'number',
        ...getColumCapabilities('holeDepth'),
        valueFormatter(_value) {
          if (_value == null) {
            return '-';
          }

          const value = Number(_value);

          if (!Number.isNaN(value)) {
            return Math.round(convertDepthUnitValue(value, defaultDepthUnit, scaleDepthUnit) * 100) / 100;
          }

          return '-';
        },
        renderHeader: (params) => (
          <TableHeader
            field={params.field}
            textTKey={FIELD_TO_AUTO_ANOMALIES_COLUMN_TITLE_KEY.holeDepth}
            unit="m"
            icon={<DepthLightCustom />}
          />
        ),
      },
      {
        ...baseColumn,
        field: 'limitType',
        type: 'singleSelect',
        ...getColumCapabilities('limitType'),
        valueOptions: [
          'MIN_LIMIT',
          'MAX_LIMIT',
          'MIN_LIMIT_PARAMETER',
          'MAX_LIMIT_PARAMETER',
          'MIN_DURATION',
          'MAX_DURATION',
          'DELTA',
        ].map((limitType) => ({
          value: limitType,
          label: t(`alertsManagerWidget:autoAnomalies.limitType.${limitType}`),
        })),
      },
      {
        ...baseColumn,
        field: 'unitId',
        type: 'singleSelect',
        ...getColumCapabilities('unitId'),
        valueOptions: service.unitsList.map((unit) => ({
          value: unit.id,
          label: unit.data['abbreviation']?.toString() ?? '',
        })),
      },
      {
        ...numberColumn,
        field: 'valueFact',
        type: 'number',
        ...getColumCapabilities('valueFact'),
      },
      {
        ...numberColumn,
        field: 'valueLimit',
        type: 'number',
        ...getColumCapabilities('valueLimit'),
      },
      {
        ...numberColumn,
        field: 'valueAbsoluteDiff',
        type: 'number',
        ...getColumCapabilities('valueAbsoluteDiff'),
      },
      {
        ...baseColumn,
        field: 'valueRelativeDiff',
        type: 'number',
        ...getColumCapabilities('valueRelativeDiff'),
        align: 'right',
        valueFormatter: (value) => {
          const number = Number(value);
          if (Number.isNaN(number) || !Number.isFinite(number)) {
            return value;
          }
          return `${Math.round(number * 100) / 100}%`;
        },
      },
      // {
      //   ...baseColumn,
      //   field: 'notification',
      //   type: 'string',
      // },
      {
        ...baseColumn,
        field: 'recommendation',
        type: 'string',
      },
      {
        ...baseColumn,
        field: 'referenceParameterName',
        type: 'string',
        ...getColumCapabilities('referenceParameterName'),
        valueGetter: (_, row) => {
          const referenceParameter = (row.minimumReferenceCurve ?? row.maximumReferenceCurve)?.parameter;

          if (!referenceParameter) {
            return '';
          }

          return isAltNames && referenceParameter.generalName
            ? referenceParameter.generalName
            : referenceParameter.displayMnemonic;
        },
      },
      {
        ...baseColumn,
        field: 'stageTypeId',
        cellClassName: tableStyles.tableCellPurple,
        type: 'singleSelect',
        ...getColumCapabilities('stageTypeId'),
        valueOptions: service.workClassifierStageTypes.map((stage) => ({
          value: stage.id,
          label: stage.data['name']?.toString() ?? '',
        })),
      },
      {
        ...baseColumn,
        field: 'wellboreTypeId',
        cellClassName: tableStyles.tableCellPurple,
        type: 'singleSelect',
        ...getColumCapabilities('wellboreTypeId'),
        valueOptions: service.wellboreTypes.map((wellbore) => ({
          value: wellbore.id,
          label: wellbore.data['customName']?.toString() ?? '',
        })),
      },
      {
        ...baseColumn,
        field: 'trajectoryTypeId',
        cellClassName: tableStyles.tableCellPurple,
        type: 'singleSelect',
        ...getColumCapabilities('trajectoryTypeId'),
        valueOptions: service.trajectoryTypes.map((trajectory) => ({
          value: trajectory.id,
          label: trajectory.data['name']?.toString() ?? '',
        })),
      },
      {
        ...baseColumn,
        field: 'sectionTypeId',
        cellClassName: tableStyles.tableCellPurple,
        type: 'singleSelect',
        ...getColumCapabilities('sectionTypeId'),
        valueOptions: service.sectionTypes.map((section) => ({
          value: section.id,
          label: section.data['name']?.toString() ?? '',
        })),
      },
      {
        ...baseColumn,
        field: 'holeType',
        cellClassName: tableStyles.tableCellPurple,
        type: 'singleSelect',
        ...getColumCapabilities('holeType'),
        valueOptions: [
          { value: 'cased', label: t('alertsManagerWidget:autoAnomalies.holeType.cased') },
          { value: 'open', label: t('alertsManagerWidget:autoAnomalies.holeType.open') },
        ],
      },
      {
        ...baseColumn,
        field: 'stratUnitTypeId',
        cellClassName: tableStyles.tableCellPurple,
        type: 'singleSelect',
        ...getColumCapabilities('stratUnitTypeId'),
        valueOptions: service.stratUnitTypes.map((stratUnit) => ({
          value: stratUnit.id,
          label: stratUnit.data['name']?.toString() ?? '',
        })),
      },
      {
        ...baseColumn,
        field: 'reservoirId',
        cellClassName: tableStyles.tableCellPurple,
        type: 'singleSelect',
        ...getColumCapabilities('reservoirId'),
        valueOptions: service.reservoirTypes.map((reservoir) => ({
          value: reservoir.id,
          label: reservoir.data['name']?.toString() ?? '',
        })),
      },
      {
        ...baseColumn,
        field: 'runTypeId',
        cellClassName: tableStyles.tableCellPurple,
        type: 'singleSelect',
        ...getColumCapabilities('runTypeId'),
        valueOptions: service.runTypes.map((run) => ({
          value: run.id,
          label: run.data['name']?.toString() ?? '',
        })),
      },
      {
        ...baseColumn,
        field: 'operationTypeId',
        cellClassName: tableStyles.tableCellPurple,
        ...getColumCapabilities('operationTypeId'),
        type: 'singleSelect',
        valueOptions: service.autosupervisorOperations.map((operation) => ({
          value: operation.id,
          label: operation.data['name']?.toString() ?? '',
        })),
      },
    ];
  }, [
    editing,
    service.wellbores.list,
    service.anomalyTypes,
    service.anomalySubtypes,
    service.unitsList,
    service.workClassifierStageTypes,
    service.wellboreTypes,
    service.trajectoryTypes,
    service.sectionTypes,
    service.stratUnitTypes,
    service.reservoirTypes,
    service.runTypes,
    service.autosupervisorOperations,
    service.autosupervisorKPIstates,
    drillingLoadsOptions,
    riskLevelOptions,
    t,
    getDateWithTimeZone,
    riskLabels,
    defaultDepthUnit,
    scaleDepthUnit,
    isAltNames,
  ]);
};
